/**
 * Business Inbox page — channel-agnostic conversation management.
 *
 * Shows all conversations across WhatsApp, web, and future channels
 * where the business owner's personas are the AI agent.
 */

import { useState, useEffect, useCallback, useRef, useMemo } from 'react'
import { useParams, useNavigate, useSearchParams } from 'react-router-dom'
import { Inbox, BarChart3, Plus } from 'lucide-react'
import { Card, Button, Badge } from '@/components/ui'
import { PageFrame } from '@/components/layout/PageFrame'
import { PageHeader } from '@/components/layout/PageHeader'
import { InboxConversationList, InboxConversationDetail, NewConversationDialog, InboxOrderSidebar, BulkArchiveDialog } from '@/components/inbox'
import { showsRecordSidebar } from '@/components/inbox/channels'
import { PanelResizeHandle } from '@/components/ui/PanelResizeHandle'
import { LIST_WIDTH, listWidth } from '@/components/inbox/listWidth'
import { InboxToolbar } from '@/components/inbox/InboxToolbar'
import { INBOX_VIEW_PANEL_ID, inboxViewTabId } from '@/components/inbox/inboxViewIds'
import { useInboxFilterChips } from '@/hooks/useInboxFilterChips'
import { useInboxQueue } from '@/hooks/useInboxQueue'
import { useRecordNoun } from '@/hooks/useRecordNoun'
import { QueueList } from '@/components/inbox/QueueList'
import { defaultSort, sortValues } from '@/components/inbox/sortText'
import { WAITING_COUNT_LABEL, chipWrites } from '@/components/inbox/inboxShape'
import { useInboxShape } from '@/hooks/useInboxShape'
import { useInbox, useInboxSSE } from '@/hooks/useInbox'
import { useFeature } from '@/hooks/useFeature'
import { usePermission } from '@/hooks/usePermission'
import { useSettingsStore } from '@/stores'
import { useInboxStore } from '@/stores/inboxStore'
import { useTypingIndicators, useVisitorTypingIndicators } from '@/hooks/useTypingIndicators'
import { EMAIL_DRAFT_KEY } from '@/hooks/useEmailDraft'
import { queryClient } from '@/services/queryClient'

export function InboxPage() {
  const { conversationId } = useParams<{ conversationId?: string }>()
  const navigate = useNavigate()
  // SA-946: `/inbox/<id>?message=<messageId>` opens the thread at a message.
  const [searchParams] = useSearchParams()
  const focusMessageId = searchParams.get('message')
  const {
    conversations,
    currentConversation,
    stats,
    isLoading,
    isLoadingDetail,
    total,
    hasMore,
    isLoadingMore,
    loadMoreError,
    fetchMore,
    channelFilter,
    statusFilter,
    searchQuery,
    fetchConversations,
    fetchConversationDetail,
    fetchStats,
    sendMessage,
    escalate,
    resumeAi,
    resolve,
    reopen,
    markAsRead,
    archiveConversation,
    unarchiveConversation,
    setChannelFilter,
    setStatusFilter,
    setSearchQuery,
    setCurrentConversation,
    createConversation,
    sortBy,
    setSortBy,
    viewTab,
    setViewTab,
    bulkArchive,
    faultsView,
    setFaultsView,
  } = useInbox()

  // SA-1092 §3: the Triage tab's queue — read beside the list wherever the
  // list is read, while that tab is open.
  const queue = useInboxQueue()
  const viewTabRef = useRef(viewTab)
  viewTabRef.current = viewTab
  const { fetchQueue } = queue
  const fetchQueueIfTriage = useCallback((opts?: { silent?: boolean }) => {
    if (viewTabRef.current === 'triage') fetchQueue(opts)
  }, [fetchQueue])

  const typingConversations = useTypingIndicators()
  const visitorTypingConversations = useVisitorTypingIndicators()
  // SA-1092 §2: the one rule that shapes the bar and the thread header.
  const shape = useInboxShape({ viewTab, channelFilter })
  // SA-715: no mailboxes, no email conversations to filter to.
  const emailEnabled = useFeature('email_ingestion')
  // SA-1091: the chip row's data (the mailbox list under its own key) and
  // its selections, read from the store so the refetch effect sees them.
  const filterChipMailboxes = useInboxFilterChips(emailEnabled)
  const kindFilter = useInboxStore((s) => s.kindFilter)
  const flagFilter = useInboxStore((s) => s.flagFilter)
  const mailboxFilter = useInboxStore((s) => s.mailboxFilter)
  // SA-1329: the queue's selection clears when the filters it was made under change.
  const recordNoun = useRecordNoun().singular.toLowerCase()
  const queueResetToken = JSON.stringify([channelFilter, kindFilter, flagFilter, mailboxFilter, searchQuery])
  const { ui: { inboxGroupBySender, inboxListWidth }, setInboxGroupBySender, setInboxListWidth } = useSettingsStore()
  // SA-1079: the list's width mid-drag, before it is stored on release.
  const [liveListWidth, setLiveListWidth] = useState<number | null>(null)
  const listColumnWidth = liveListWidth ?? listWidth(inboxListWidth)
  const [showNewConversation, setShowNewConversation] = useState(false)
  const [newConvDefaults, setNewConvDefaults] = useState<{ phone?: string; name?: string }>({})
  const [reviewFilter, setReviewFilter] = useState(false)
  const [showBulkArchive, setShowBulkArchive] = useState(false)

  // Track the current conversationId in a ref so the SSE callback stays stable
  const conversationIdRef = useRef(conversationId)
  conversationIdRef.current = conversationId

  // Debounced refetch to avoid redundant API calls from rapid SSE events
  const debouncedRefetch = useMemo(() => {
    let listTimer: ReturnType<typeof setTimeout> | null = null
    const detailTimers = new Map<string, ReturnType<typeof setTimeout>>()

    return {
      list: () => {
        if (listTimer) clearTimeout(listTimer)
        listTimer = setTimeout(() => {
          fetchConversations({ silent: true })
          fetchQueueIfTriage({ silent: true })
          fetchStats()
        }, 150)
      },
      detail: (convId: string) => {
        const existing = detailTimers.get(convId)
        if (existing) clearTimeout(existing)
        detailTimers.set(convId, setTimeout(() => {
          detailTimers.delete(convId)
          // Only refetch if still viewing this conversation (silent = no loading spinner)
          if (conversationIdRef.current === convId) {
            fetchConversationDetail(convId, { silent: true })
          }
        }, 100))
      },
    }
  }, [fetchConversations, fetchStats, fetchConversationDetail, fetchQueueIfTriage])

  // SSE real-time updates
  const handleSSEEvent = useCallback((event: { type: string; conversationId?: string; messageId?: string }) => {
    switch (event.type) {
      case 'new_message':
        debouncedRefetch.list()
        if (event.conversationId) {
          debouncedRefetch.detail(event.conversationId)
          // Auto-mark as read if the user is currently viewing this conversation
          if (conversationIdRef.current === event.conversationId) {
            markAsRead(event.conversationId)
          }
        }
        break
      case 'new_conversation':
        debouncedRefetch.list()
        break
      case 'conversation_updated':
        debouncedRefetch.list()
        if (event.conversationId) {
          debouncedRefetch.detail(event.conversationId)
          // SA-1040 PR 2: an edit of a quote rewords its offer email and says so here; the open
          // thread's draft is refetched, so its panel never keeps (and saves) the stale text. On the
          // app's one client, as `useInboxFilterChips` reads it: the page holds no provider of its own.
          if (conversationIdRef.current === event.conversationId) {
            void queryClient.invalidateQueries({ queryKey: [...EMAIL_DRAFT_KEY, event.conversationId] })
          }
        }
        break
      case 'call_started':
      case 'call_ended':
        debouncedRefetch.list()
        if (event.conversationId) {
          debouncedRefetch.detail(event.conversationId)
        }
        break
      case 'call_transcript':
        // A new turn was persisted — refresh the detail view to show it
        if (event.conversationId) {
          debouncedRefetch.detail(event.conversationId)
        }
        break
    }
  }, [debouncedRefetch, markAsRead])

  useInboxSSE(handleSSEEvent)

  // Polling fallback: refresh list every 30s in case SSE connection drops
  // Use silent mode to avoid loading spinners and layout jumps
  useEffect(() => {
    const interval = setInterval(() => {
      fetchConversations({ silent: true })
      fetchQueueIfTriage({ silent: true })
      fetchStats()
    }, 30000)
    return () => clearInterval(interval)
  }, [fetchConversations, fetchStats, fetchQueueIfTriage])

  // One list fetch per change, at mount included. `keep`: when the pages
  // already loaded answer these parameters (a return from the Triage tab, a
  // remount) the head merges over them quietly instead of replacing them.
  useEffect(() => {
    fetchConversations({ keep: true })
    fetchQueueIfTriage()
  }, [channelFilter, statusFilter, kindFilter, flagFilter, mailboxFilter, searchQuery, sortBy, viewTab, faultsView]) // eslint-disable-line react-hooks/exhaustive-deps

  // Stats are read once, on mount.
  useEffect(() => {
    fetchStats()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // SA-1092 §2.2: a control the shape hides holds no selection — once the
  // shape is known (the loading shape hides everything and decides nothing).
  // SA-1104: a control only the Triage tab hides keeps its selection for
  // Open, as the sort does; the queue ignores it.
  useEffect(() => {
    if (shape.kind === 'loading' || viewTab === 'triage') return
    if (!shape.statusRow && statusFilter) setStatusFilter(null)
    if (!shape.faults && faultsView) setFaultsView(false)
    if (!shape.needsReview && reviewFilter) setReviewFilter(false)
  }, [shape.kind, shape.statusRow, shape.faults, shape.needsReview, viewTab]) // eslint-disable-line react-hooks/exhaustive-deps

  // SA-1092 §4.1: the channel chip sets the sort options AND the default —
  // a chip change takes the chip's default when the chip's option SET
  // changes (Email ↔ WhatsApp group on a desk); a console's chips share one
  // set, so its sort is never reset (R2).
  const sortSetRef = useRef<string | null>(null)
  useEffect(() => {
    const key = sortValues(channelFilter, shape.kind)
    if (sortSetRef.current !== null && sortSetRef.current !== key) setSortBy(defaultSort(channelFilter, shape.kind))
    sortSetRef.current = key
  }, [channelFilter, shape.kind]) // eslint-disable-line react-hooks/exhaustive-deps

  // SA-1104: a chip change is ONE store write — the chip, the console's
  // selections the new chip hides and its default sort together — so the
  // list fetches once; the two effects above then find nothing to write.
  const selectChannel = useCallback((value: string | null) => {
    const { clearConsole, sortBy: nextSort } = chipWrites(shape.kind, channelFilter, value)
    setChannelFilter(value, {
      ...(clearConsole ? { statusFilter: null, faultsView: false } : {}),
      ...(nextSort !== null ? { sortBy: nextSort } : {}),
    })
    if (clearConsole) setReviewFilter(false)
  }, [shape.kind, channelFilter, setChannelFilter])

  // Load conversation detail when URL changes
  useEffect(() => {
    if (conversationId) {
      fetchConversationDetail(conversationId)
    } else {
      setCurrentConversation(null)
    }
  }, [conversationId]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleSelectConversation = useCallback((id: string) => {
    navigate(`/inbox/${id}`)
  }, [navigate])

  const handleSendMessage = useCallback(async (content: string, mediaRef?: string) => {
    if (!conversationId) return
    return sendMessage(conversationId, content, mediaRef)
  }, [conversationId, sendMessage])

  // Silent refetch on all four: these refresh the conversation already on
  // screen, so the non-silent variant swapped the whole pane for a spinner —
  // a visible flash on every click, and the reason Resolve left the thread
  // scrolled away from the newest message. Matches handleRefetchDetailAndList
  // and handleArchive below. Each action's own failure is reported via the
  // store; only the follow-up refetch is silent (see useInbox.ts), and a
  // blipped GET self-heals — the server's conversation_updated broadcast
  // re-fires another silent refetch ~100ms later.
  const handleEscalate = useCallback(async () => {
    if (!conversationId) return
    await escalate(conversationId)
    fetchConversationDetail(conversationId, { silent: true })
  }, [conversationId, escalate, fetchConversationDetail])

  const handleResumeAi = useCallback(async () => {
    if (!conversationId) return
    await resumeAi(conversationId)
    fetchConversationDetail(conversationId, { silent: true })
  }, [conversationId, resumeAi, fetchConversationDetail])

  const handleResolve = useCallback(async () => {
    if (!conversationId) return
    await resolve(conversationId)
    fetchConversationDetail(conversationId, { silent: true })
  }, [conversationId, resolve, fetchConversationDetail])

  const handleReopen = useCallback(async () => {
    if (!conversationId) return
    await reopen(conversationId)
    fetchConversationDetail(conversationId, { silent: true })
  }, [conversationId, reopen, fetchConversationDetail])

  const handleMarkRead = useCallback(async () => {
    if (!conversationId) return
    await markAsRead(conversationId)
  }, [conversationId, markAsRead])

  const canArchive = usePermission('inbox:archive')

  const handleArchive = useCallback(async () => {
    if (!conversationId) return
    if (await archiveConversation(conversationId)) {
      // Keep the detail open; refresh so the button flips to Unarchive.
      fetchConversationDetail(conversationId, { silent: true })
    }
  }, [conversationId, archiveConversation, fetchConversationDetail])

  const handleUnarchive = useCallback(async () => {
    if (!conversationId) return
    if (await unarchiveConversation(conversationId)) {
      fetchConversationDetail(conversationId, { silent: true })
    }
  }, [conversationId, unarchiveConversation, fetchConversationDetail])

  const handleRefetchDetailAndList = useCallback(() => {
    fetchConversations({ silent: true })
    fetchQueueIfTriage({ silent: true })
    if (conversationId) fetchConversationDetail(conversationId, { silent: true })
  }, [conversationId, fetchConversations, fetchConversationDetail, fetchQueueIfTriage])

  // SA-717: a triage decision moves the thread, the list and the tab's count.
  const [triageRefresh, setTriageRefresh] = useState(0)
  const handleTriageChanged = useCallback(() => {
    setTriageRefresh((n) => n + 1)
    handleRefetchDetailAndList()
    fetchStats()
  }, [handleRefetchDetailAndList, fetchStats])

  // SA-1329: rows of a bulk run settling move the thread, the list and the queue.
  // Only an increase over the count last seen counts: a remount while a finished
  // run sits in the store must not refetch for rows settled long ago. The queue is
  // refetched through handleTriageChanged on the Triage tab and on entering it.
  const runSettled = useInboxStore((s) => s.triageRun?.settled ?? 0)
  const runId = useInboxStore((s) => s.triageRun?.id ?? null)
  const handleTriageChangedRef = useRef(handleTriageChanged)
  handleTriageChangedRef.current = handleTriageChanged
  const settledSeen = useRef({ id: runId, settled: runSettled })
  useEffect(() => {
    // Keyed on the run as well as the count: a new run may reach the last run's count in one batch.
    const seen = settledSeen.current
    const increased = runId === seen.id ? runSettled > seen.settled : runSettled > 0
    settledSeen.current = { id: runId, settled: runSettled }
    if (increased) handleTriageChangedRef.current()
  }, [runId, runSettled])

  const handleMarkReviewed = useCallback(() => {
    handleRefetchDetailAndList()
  }, [handleRefetchDetailAndList])

  const handleViewPreviousConversation = useCallback((id: string) => {
    navigate(`/inbox/${id}`)
  }, [navigate])

  const handleNewConversationForCustomer = useCallback(() => {
    if (!currentConversation) return
    setNewConvDefaults({
      phone: currentConversation.customerPhone || undefined,
      name: currentConversation.customerDisplayName || undefined,
    })
    setShowNewConversation(true)
  }, [currentConversation])

  const handleCreateConversation = useCallback(async (data: {
    personaId: string
    customerPhone: string
    customerDisplayName?: string
    initialMessage: string
    confirmMultiplePersonas?: boolean
  }) => {
    const result = await createConversation({
      ...data,
      channel: 'whatsapp',
    })
    if (result.ok) {
      fetchConversations()
      fetchStats()
      navigate(`/inbox/${result.conversation.id}`)
      return { ok: true as const, conversationId: result.conversation.id }
    }
    return result
  }, [createConversation, fetchConversations, fetchStats, navigate])

  return (
    <PageFrame width="list" className="flex h-full flex-col gap-4">
      <PageHeader
        title="Inbox"
        icon={Inbox}
        subtitle={stats && (
          <span className="flex flex-wrap items-center gap-x-4 gap-y-1">
            {/* The counts lead; a badge shows only when it counts something (SA-1125: in the
                subtitle an invisible placeholder badge opened the line with a gap). */}
            {(shape.headCount === 'waiting' || shape.headCount === 'both') && (
              <span>{WAITING_COUNT_LABEL(stats.waitingItems)}</span>
            )}
            {(shape.headCount === 'console' || shape.headCount === 'both') && (
              <>
                <span>{stats.activeConversations} active</span>
                <span>{stats.totalConversations} total</span>
              </>
            )}
            {stats.totalUnread > 0 && (
              <Badge variant="default" className="text-xs">{stats.totalUnread} unread</Badge>
            )}
            {stats.humanConversations > 0 && (
              <Badge variant="destructive" className="text-xs">{stats.humanConversations} escalated</Badge>
            )}
          </span>
        )}
        actions={
          <>
            {shape.newConversation && (
              <Button
                variant="default"
                size="sm"
                className="text-xs h-8"
                onClick={() => {
                  setNewConvDefaults({})
                  setShowNewConversation(true)
                }}
              >
                <Plus className="w-3.5 h-3.5 mr-1" />
                New Conversation
              </Button>
            )}
            <Button
              variant="ghost"
              size="sm"
              className="text-xs h-8"
              onClick={() => navigate('/inbox/dashboard')}
            >
              <BarChart3 className="w-3.5 h-3.5 mr-1" />
              Dashboard
            </Button>
          </>
        }
      />

      {/* SA-1127: one card holds the toolbar, the list and the pane (R4). */}
      <Card className="min-h-0 flex-1 gap-0 overflow-hidden py-0">
        <InboxToolbar
          shape={shape}
          viewTab={viewTab}
          onViewTab={setViewTab}
          triageCount={shape.tabs.includes('triage') ? (stats?.waitingItems ?? 0) : undefined}
          searchQuery={searchQuery}
          onSearch={setSearchQuery}
          channelFilter={channelFilter}
          onChannel={selectChannel}
          statusFilter={statusFilter}
          onStatus={setStatusFilter}
          reviewFilter={reviewFilter}
          onReview={setReviewFilter}
          faultsView={faultsView}
          onFaults={setFaultsView}
          faultCount={stats?.faultConversations ?? 0}
          sortBy={sortBy}
          onSort={setSortBy}
          groupBySender={inboxGroupBySender}
          onGroupBySender={setInboxGroupBySender}
          canArchive={canArchive}
          onBulkArchive={() => setShowBulkArchive(true)}
          mailboxes={filterChipMailboxes}
        />

        {/* Split layout: list + detail */}
        <div className="flex min-h-0 flex-1">
          {/* Conversation list (left panel). SA-1079: the list is the
              column with a width; the chain beside it stays flex-1. The
              handle's 1px line is the column's right border, so the
              border-r goes (below `sm` the handle hides and the border
              comes back). SA-1129: it is the panel the view tabs control. */}
          <div data-testid="conversation-list-column" className="relative shrink-0 overflow-hidden max-sm:border-r max-sm:border-border"
            id={INBOX_VIEW_PANEL_ID} role="tabpanel" aria-labelledby={inboxViewTabId(viewTab)}
            style={{ width: listColumnWidth }}>
            {viewTab === 'triage' ? (
              /* SA-1092 §3: the Triage tab is the queue — one row per waiting
                 item; a group item opens the thread at its message. */
              <QueueList
                items={queue.items} total={queue.total} isLoading={queue.isLoading} error={queue.error} arrivedIds={queue.arrivedIds}
                selected={{ conversationId: conversationId ?? null, messageId: focusMessageId }}
                onSelect={(item) => navigate(item.messageId ? `/inbox/${item.conversationId}?message=${encodeURIComponent(item.messageId)}` : `/inbox/${item.conversationId}`)}
                onLoadMore={queue.fetchMore} onClearArrivals={queue.clearArrivals} hasMore={queue.items.length < queue.total}
                noun={recordNoun} resetToken={queueResetToken}
              />
            ) : (
            <InboxConversationList
              conversations={reviewFilter ? conversations.filter((c) => c.bankTransferReviewRequired) : conversations}
              filtersActive={reviewFilter || faultsView}
              selectedId={conversationId || null}
              isLoading={isLoading && conversations.length === 0}
              onSelect={handleSelectConversation}
              groupBySender={inboxGroupBySender}
              total={total}
              hasMore={hasMore}
              isLoadingMore={isLoadingMore}
              loadMoreError={loadMoreError}
              onLoadMore={fetchMore}
            />
            )}
            <PanelResizeHandle edge="right" width={listColumnWidth} min={LIST_WIDTH.min} max={LIST_WIDTH.max}
              label="Resize conversation list" className="max-sm:hidden"
              onLive={setLiveListWidth} onCommit={setInboxListWidth} onReset={() => setInboxListWidth(null)} />
          </div>

          {/* Conversation detail (right panel) */}
          <InboxConversationDetail
            conversation={currentConversation}
            isLoading={isLoadingDetail}
            onSendMessage={handleSendMessage}
            onEscalate={handleEscalate}
            onResumeAi={handleResumeAi}
            onResolve={handleResolve}
            onReopen={handleReopen}
            onMarkRead={handleMarkRead}
            onViewPreviousConversation={handleViewPreviousConversation}
            onNewConversation={shape.newConversation ? handleNewConversationForCustomer : undefined}
            onBankTransferDecision={handleRefetchDetailAndList}
            onMarkReviewed={handleMarkReviewed}
            onTriageChanged={handleTriageChanged}
            onArchive={canArchive ? handleArchive : undefined}
            onUnarchive={canArchive ? handleUnarchive : undefined}
            focusMessageId={focusMessageId}
            aiTyping={!!(currentConversation && typingConversations[currentConversation.id])}
            visitorTyping={!!(currentConversation && visitorTypingConversations[currentConversation.id])}
          />

          {/* Record sidebar (far right; renders nothing when the conversation has no record).
              SA-1095: only an email thread names its record and its records (the newest open
              one and a switcher); any other channel keeps the read by conversation. */}
          {currentConversation && showsRecordSidebar(currentConversation) && (
            <InboxOrderSidebar
              conversationId={currentConversation.id}
              recordId={currentConversation.channel === 'email' ? currentConversation.linkedOrderId : undefined}
              records={currentConversation.channel === 'email' ? currentConversation.records : undefined}
              refreshToken={`${triageRefresh}:${currentConversation.linkedOrderId ?? ''}`}
            />
          )}
        </div>
      </Card>

      {/* New conversation dialog */}
      {shape.newConversation && (
        <NewConversationDialog
          open={showNewConversation}
          onOpenChange={setShowNewConversation}
          onSubmit={handleCreateConversation}
          defaultCustomerPhone={newConvDefaults.phone}
          defaultCustomerName={newConvDefaults.name}
        />
      )}

      <BulkArchiveDialog
        open={showBulkArchive}
        onOpenChange={setShowBulkArchive}
        onDryRun={(days) => bulkArchive(days, true)}
        onConfirm={(days) => bulkArchive(days, false)}
      />
    </PageFrame>
  )
}
